/**
 * Tutor support form section.
 * Programs, assignment (departments / establishments / validate checkboxes), subjects, availability, engagement dates.
 */
import { Building2, Calendar, CalendarClock, Handshake } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { DatePicker } from '@/components/forms/DatePicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { ProgramCascadeFields } from '@/components/common/ProgramCascadeFields';
import { ALLOWED_INPERSON_TUTOR_DEPARTMENT_CODES } from '@/components/tutors/tutor-configs';

/** Props for {@link TutorSupportSection}. */
interface Props {
  mode: 'create' | 'edit';
  formData: Record<string, any>;
  fullState: unknown;
  programsData: any[];
  subjectOptions: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  establishmentOptions: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  selectedDepartments: string[];
  isLoadingEstablishments: boolean;
  allowedSlots: any;
  tutorPageActions: any;
  set: (field: string, value: any) => void;
  setEstablishmentSearch: (search: string) => void;
  engagementEndDateError: string | undefined;
}

/** Tutor support form section. */
export function TutorSupportSection({
  mode,
  formData,
  fullState,
  programsData,
  subjectOptions,
  establishmentOptions,
  selectedDepartments,
  isLoadingEstablishments,
  allowedSlots,
  tutorPageActions,
  set,
  setEstablishmentSearch,
  engagementEndDateError,
}: Readonly<Props>) {
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Handshake className="w-4 h-4" />
        Disponibilité et Matching
      </h4>

      <ProgramCascadeFields
        programs={programsData as any[]}
        value={{
          tutoringType: 'in_person',
          coursePath: formData.coursePath,
          programs: formData.programs,
        }}
        onChange={(field, value) => {
          if (field !== 'tutoringType') set(field, value);
        }}
        showTutoringType={false}
      />

      <div className="form-section">
        <h4 className="form-section-title">
          <Building2 className="w-4 h-4" />
          Affectation
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
          <DepartmentSelect
            label="Département d'affectation"
            value={formData.departments?.[0] ?? ''}
            onChange={(val) => set('departments', val ? [val] : [])}
            placeholder="Sélectionner un département"
            inputSize="sm"
            allowedCodes={ALLOWED_INPERSON_TUTOR_DEPARTMENT_CODES}
          />
          <MultiSelect
            label="Établissements souhaités (max 3)"
            value={formData.selectedEstablishments ?? []}
            onChange={(val) => set('selectedEstablishments', val.slice(0, 3))}
            options={establishmentOptions as any[]}
            placeholder={
              selectedDepartments.length === 0
                ? "Sélectionner d'abord les départements"
                : "Sélectionner jusqu'à 3 établissements"
            }
            disabled={selectedDepartments.length === 0 || isLoadingEstablishments}
            inputSize="sm"
            showSelectAll={false}
            onSearch={setEstablishmentSearch}
            searchPlaceholder="Rechercher un établissement..."
            isSearchLoading={isLoadingEstablishments}
          />
        </div>

        {(tutorPageActions.canPrevalidate || tutorPageActions.canValidateAssignment) &&
          (formData.selectedEstablishments ?? []).length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-start mt-3">
              {tutorPageActions.canPrevalidate && (
                <Checkbox
                  label="Prévalider l'affectation"
                  helperText="Marque directement les établissements sélectionnés comme retenus (recruteur)."
                  checked={formData.preValidated === true}
                  onCheckedChange={(c) => set('preValidated', c)}
                  size="sm"
                  disabled={
                    mode === 'edit' &&
                    (fullState as any)?.tutorSituation === 'awaiting_coordinator_validation'
                  }
                />
              )}
              {tutorPageActions.canValidateAssignment && (
                <Checkbox
                  label="Valider l'affectation directement"
                  helperText="Affecte définitivement le tuteur sans passer par la prévalidation recruteur (coordinateur)."
                  checked={formData.validated === true}
                  onCheckedChange={(c) => {
                    set('validated', c);
                    if (c) set('preValidated', true);
                  }}
                  size="sm"
                  disabled={
                    (mode === 'edit' && (fullState as any)?.tutorSituation === 'active') ||
                    (tutorPageActions.canPrevalidate && !formData.preValidated)
                  }
                />
              )}
            </div>
          )}
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <CalendarClock className="w-4 h-4" />
          Matières &amp; Disponibilités
        </h4>
        <MultiSelect
          label="Matières"
          value={formData.subjects ?? []}
          onChange={(val) => set('subjects', val)}
          options={subjectOptions as any[]}
          placeholder="Sélectionner les matières"
          helperText="Plusieurs choix possibles"
          required
          inputSize="sm"
        />
        <fieldset className="border-0 p-0 m-0 min-w-0">
          <legend className="text-sm font-medium text-foreground mb-2 block">
            Créneaux de disponibilité <span className="text-destructive">*</span>
          </legend>
          <AvailabilityGrid
            value={
              formData.availability &&
              typeof formData.availability === 'object' &&
              'weekly' in formData.availability
                ? (formData.availability as any)
                : { weekly: [] }
            }
            onChange={(val) => set('availability', val)}
            allowedSlots={allowedSlots}
          />
        </fieldset>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Calendar className="w-4 h-4" />
          Engagement
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <DatePicker
            label="Date de début"
            placeholder="JJ/MM/AAAA"
            value={formData.engagementStartDate ?? ''}
            onChange={(val) => set('engagementStartDate', val)}
            inputSize="sm"
            required
          />
          <DatePicker
            label="Date de fin"
            placeholder="JJ/MM/AAAA"
            value={formData.engagementEndDate ?? ''}
            onChange={(val) => set('engagementEndDate', val)}
            inputSize="sm"
            minDate={formData.engagementStartDate || undefined}
            state={engagementEndDateError ? 'error' : undefined}
            error={engagementEndDateError}
            required
          />
          <Input
            label="Objectif d'heures"
            type="number"
            placeholder="Ex: 20"
            value={formData.targetHours ?? ''}
            onChange={(e) => set('targetHours', e.target.value)}
            inputSize="sm"
          />
        </div>
      </div>
    </div>
  );
}
